import { generatePath } from '@helpers/utils/paths/paths';
import { z } from 'zod';

const watchlistSummarySchema = z.object({
  summary: z.string(),
});

const dailyBriefQuestionDrawerParamsSchema = z.record(z.string(), z.string());

const dailyBriefQuestionContextInlineSchema = z.object({
  text: z.string(),
  sourceNumber: z.number().optional(),
  sourceType: z.string().optional(),
  sourceId: z.string().optional(),
  drawerParams: dailyBriefQuestionDrawerParamsSchema.optional(),
});

const dailyBriefQuestionSourceSchema = z.object({
  sourceNumber: z.number(),
  sourceType: z.string(),
  insightType: z.string().optional(),
  sourceId: z.string(),
  title: z.string().optional(),
  drawerParams: dailyBriefQuestionDrawerParamsSchema.optional(),
});

const dailyBriefQuestionSchema = z.object({
  sectionTitle: z.string(),
  subsectionTitle: z.string(),
  watchlistId: z.string(),
  watchlistIds: z.array(z.string()).optional(),
  dedupeKey: z.string().optional(),
  question: z.string().optional(),
  questionContext: z.string(),
  personalizationEvidence: z.array(z.string()).optional(),
  questionContextInlines: z
    .array(dailyBriefQuestionContextInlineSchema)
    .optional(),
  sources: z.array(dailyBriefQuestionSourceSchema).optional(),
});

const dailyBriefQuestionListSchema = z.array(dailyBriefQuestionSchema);

const dailyBriefQuestionsSchema = z.object({
  questions: dailyBriefQuestionListSchema,
});

const dailyBriefResponseSchema = z.object({
  status: z.enum(['queued']),
  jobId: z.string(),
});

const dailyBriefStatusSchema = z.object({
  id: z.string(),
  watchlistId: z.string(),
  status: z.enum(['queued', 'generating', 'done', 'failed']),
});

export const watchlistSummary = {
  getWatchlistSummary: {
    path: (watchlistId: string) => `watchlist-summary/v1/${watchlistId}`,
    schema: watchlistSummarySchema,
  },
  getDailyBriefQuestions: {
    path: (watchlistId?: string) => {
      const path = 'watchlist-summary/v1/daily-brief/questions';

      if (!watchlistId) {
        return path;
      }

      return generatePath({
        basePath: path,
        params: { id: watchlistId },
      });
    },
    schema: dailyBriefQuestionsSchema,
  },
  getPersonalizedDailyBriefQuestions: {
    path: (watchlistId?: string) =>
      generatePath({
        basePath: 'watchlist-summary/v2/daily-brief/questions',
        params: {
          personalize: 'true',
          ...(watchlistId ? { id: watchlistId } : {}),
        },
      }),
    schema: dailyBriefQuestionsSchema,
  },
  postDailyBrief: {
    path: (watchlistId: string) =>
      `watchlist-summary/v1/${watchlistId}/daily-brief`,
    schema: dailyBriefResponseSchema,
  },
  getDailyBriefStatus: {
    path: (jobId: string) => `watchlist-summary/v1/daily-brief/jobs/${jobId}`,
    schema: dailyBriefStatusSchema,
  },
};

export type WatchlistSummary = z.infer<typeof watchlistSummarySchema>;
export type DailyBriefQuestion = z.infer<typeof dailyBriefQuestionSchema>;
export type DailyBriefQuestionDrawerParams = z.infer<
  typeof dailyBriefQuestionDrawerParamsSchema
>;
export type DailyBriefQuestionSource = z.infer<
  typeof dailyBriefQuestionSourceSchema
>;
export type DailyBriefQuestions = z.infer<typeof dailyBriefQuestionListSchema>;
export type DailyBriefQuestionsResponse = z.infer<
  typeof dailyBriefQuestionsSchema
>;
